// src/features/service-providers/api.js
// CSV-based data source:
//  1) /data/Merged_No_Data/Standard/state_5y_50k_standard_merged.csv  -> full US state list (same as Market Analysis)
//  2) /data/Merged_No_Data/Standard/metro_5y_50k_standard_merged.csv  -> full US metro list (same as Market Analysis)
//  3) /data/service_providers.csv       -> your editable providers DB in long format
//
// Exports used by ServiceProvidersPage:
//   fetchStates(), fetchMetros(), fetchCategories(), fetchServicesForCategory(), fetchProviders({ metro, category })

import Papa from "papaparse";

export const CATEGORIES = [
  "Real Estate agent",
  "Photographer",
  "Staging Services",
  "Lenders",
  "Appraisers",
  "Lawyers",
  "Moving Services",
];

const SERVICES_BY_CATEGORY = {
  "Real Estate agent": ["Commission (Seller %)", "Commission (Buyer %)"],
  "Photographer": ["Base shoot (up to 25 photos)", "Add-on: Drone/Aerial", "Add-on: Twilight set"],
  "Staging Services": ["Consultation", "Per-room staging", "Full-home staging"],
  "Lenders": ["Origination fee ($)", "Discount points (%)", "APR (illustrative)"],
  "Appraisers": ["Standard home appraisal"],
  "Lawyers": ["Closing package (flat $)", "Hourly rate ($/hr)"],
  "Moving Services": ["Local move (2BR, est.)", "Hourly crew (2 movers + truck)"],
};

let _metrosCache = null;      // string[]
let _statesCache = null;      // string[]
let _providersCache = null;   // raw long rows from CSV

// ---------- helpers ----------
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const norm = (s) => String(s || "").trim().toLowerCase();

function groupFirmRows(rows) {
  // Convert long rows [{..., service, quote}] into firm-level rows with quotes map.
  const map = new Map(); // key = metro||category||firm
  for (const r of rows) {
    const key = `${r.metro}||${r.category}||${r.firm}`;
    const prev = map.get(key) || {
      metro: r.metro,
      category: r.category,
      firm: r.firm,
      website: r.website || "",
      contact: r.contact || "",
      rating: r.rating != null && r.rating !== "" ? Number(r.rating) : null,
      ratingCount: r.ratingCount != null && r.ratingCount !== "" ? Number(r.ratingCount) : null,
      quotes: {},
    };
    if (r.service && r.quote) prev.quotes[r.service] = r.quote;
    map.set(key, prev);
  }
  return Array.from(map.values());
}

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function randomInt(a, b) { return Math.floor(Math.random() * (b - a + 1)) + a; }
function money(min, max, step = 25) {
  const v = Math.round(randomInt(min, max) / step) * step;
  return `$${v}`;
}
function percent(min = 1.0, max = 2.5, step = 0.25) {
  const raw = min + Math.random() * (max - min);
  const rounded = Math.round(raw / step) * step;
  return `${rounded.toFixed(2).replace(/\.00$/, "")}%`;
}
function buildAutoQuotes(category) {
  const svcs = SERVICES_BY_CATEGORY[category] || [];
  const q = {};
  for (const s of svcs) {
    if (category === "Real Estate agent") q[s] = percent(1.0, 2.5, 0.25);
    else if (category === "Photographer")  q[s] = money(150, 350, 25);
    else if (category === "Staging Services") q[s] = money(150, 2500, 50);
    else if (category === "Lenders") q[s] = s.includes("%") ? percent(0.0, 1.0, 0.125) : money(500, 1500, 25);
    else if (category === "Appraisers") q[s] = money(350, 800, 25);
    else if (category === "Lawyers") q[s] = s.includes("hr") ? `$${randomInt(200, 400)}/hr` : money(900, 1800, 50);
    else if (category === "Moving Services") q[s] = s.includes("Hourly") ? `$${randomInt(110, 160)}/hr` : money(700, 1400, 50);
    else q[s] = "Quote on request";
  }
  return q;
}
function autoSeed(metro, category, count = 3) {
  const nouns = {
    "Real Estate agent": ["Realty", "Homes", "Property Group", "Estates"],
    "Photographer": ["Media", "Studios", "PhotoWorks", "Creative"],
    "Staging Services": ["Staging", "Interiors", "Design", "Home Prep"],
    "Lenders": ["Home Loans", "Capital", "Funding", "Lending"],
    "Appraisers": ["Valuations", "Appraisal Group", "Analytics"],
    "Lawyers": ["Law", "Legal", "Attorneys", "Counsel"],
    "Moving Services": ["Movers", "Logistics", "Relocation", "Haul"],
  }[category] || ["Services"];

  return Array.from({ length: count }).map((_, i) => {
    const base = `${String(metro).split(",")[0]} ${pick(nouns)}`;
    return {
      metro,
      category,
      firm: i === 0 ? base : `${base} ${i + 1}`,
      website: `https://example.com/${encodeURIComponent(base.replace(/\s+/g, "-").toLowerCase())}`,
      contact: `(555) ${randomInt(100, 999)}-${String(randomInt(0, 9999)).padStart(4, "0")}`,
      rating: Number((Math.random() * 1.2 + 3.8).toFixed(1)),
      ratingCount: randomInt(25, 480),
      quotes: buildAutoQuotes(category),
    };
  });
}

// ---------- data loaders ----------
async function loadStatesFromZillow() {
  if (_statesCache) return _statesCache;
  const res = await fetch("/data/Merged_No_Data/Standard/state_5y_50k_standard_merged.csv");
  const text = await res.text();
  const parsed = Papa.parse(text, { header: true, dynamicTyping: true });
  const set = new Set();
  for (const row of parsed.data) {
    const s = row?.StateName || row?.State;
    if (s) set.add(String(s).trim());
  }
  _statesCache = Array.from(set).sort((a, b) => a.localeCompare(b));
  return _statesCache;
}

async function loadMetrosFromZillow() {
  if (_metrosCache) return _metrosCache;
  const res = await fetch("/data/Merged_No_Data/Standard/metro_5y_50k_standard_merged.csv");
  const text = await res.text();
  const parsed = Papa.parse(text, { header: true, dynamicTyping: true });
  const set = new Set();
  for (const row of parsed.data) {
    const m = row?.Metro;
    if (m) set.add(String(m).trim());
  }
  _metrosCache = Array.from(set).sort((a, b) => a.localeCompare(b));
  return _metrosCache;
}

async function loadProvidersCsv() {
  if (_providersCache) return _providersCache;
  try {
    const res = await fetch("/data/service_providers.csv");
    const text = await res.text();
    const parsed = Papa.parse(text, { header: true, dynamicTyping: true });
    _providersCache = (parsed.data || [])
      .filter(r => r.metro && r.category && r.firm)
      .map(r => ({
        metro: String(r.metro).trim(),
        category: String(r.category).trim(),
        firm: String(r.firm).trim(),
        website: r.website ? String(r.website).trim() : "",
        contact: r.contact ? String(r.contact).trim() : "",
        rating: r.rating != null && r.rating !== "" ? Number(r.rating) : null,
        ratingCount: r.ratingCount != null && r.ratingCount !== "" ? Number(r.ratingCount) : null,
        service: r.service ? String(r.service).trim() : "",
        quote: r.quote ? String(r.quote).trim() : "",
      }));
  } catch {
    _providersCache = [];
  }
  return _providersCache;
}

// ---------- public API ----------
export async function fetchStates() {
  await sleep(60);
  return loadStatesFromZillow();
}

export async function fetchMetros() {
  await sleep(60);
  return loadMetrosFromZillow();
}

export async function fetchCategories() {
  await sleep(40);
  return [...CATEGORIES];
}

export async function fetchServicesForCategory(category) {
  await sleep(40);
  return SERVICES_BY_CATEGORY[category] ? [...SERVICES_BY_CATEGORY[category]] : [];
}

export async function fetchProviders({ metro, category }) {
  await sleep(80);
  const allRows = await loadProvidersCsv();

  let rows = allRows;
  if (metro) rows = rows.filter(r => norm(r.metro) === norm(metro));
  if (category) rows = rows.filter(r => norm(r.category) === norm(category));

  let firms = groupFirmRows(rows);

  // If no firm rows exist yet for this metro/category, auto-seed mock data so UI still works
  if ((!firms || firms.length === 0) && metro && category) {
    firms = autoSeed(metro, category, 3);
  }

  // Sort by rating desc then firm asc
  firms.sort((a, b) => (Number(b.rating || 0) - Number(a.rating || 0)) || String(a.firm).localeCompare(String(b.firm)));
  return firms;
}
